import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import API_BASE_URL from '../ScrapFlow/config/config.server';

const PUSH_NOTIFICATIONS_GUARD_KEY = '__scrapflowPushNotificationsInitialized__';
let pushInitPromise = null;
let listenersConfigured = false;
let registrationListenerHandle = null;
let registrationErrorListenerHandle = null;
let notificationReceivedListenerHandle = null;
let notificationActionListenerHandle = null;
let notificationHandlers = [];
let lastSessionToken = null;

const getPushGuardState = () => {
  if (typeof window === 'undefined') return null;
  return window[PUSH_NOTIFICATIONS_GUARD_KEY];
};

const setPushGuardState = (value) => {
  if (typeof window !== 'undefined') {
    window[PUSH_NOTIFICATIONS_GUARD_KEY] = value;
  }
};

const notifyNotificationHandlers = (notification, source = 'received') => {
  notificationHandlers.forEach((handler) => {
    try {
      handler(notification, source);
    } catch (handlerError) {
      console.warn('Push notification handler failed:', handlerError);
    }
  });
};

export const registerPushNotificationHandler = (handler) => {
  if (typeof handler !== 'function') return null;
  notificationHandlers.push(handler);
  return () => {
    notificationHandlers = notificationHandlers.filter((registeredHandler) => registeredHandler !== handler);
  };
};

export const unregisterPushNotificationHandler = (handler) => {
  notificationHandlers = notificationHandlers.filter((registeredHandler) => registeredHandler !== handler);
};

/**
 * Register Firebase Cloud Messaging (FCM) token with backend
 * Call this after successful login
 */
export const registerPushToken = async (fcmToken, sessionToken) => {
  try {
    if (!fcmToken) {
      console.warn('⚠️ No FCM token provided');
      return false;
    }

    if (!sessionToken) {
      console.warn('⚠️ No session token provided for authentication');
      return false;
    }

    const response = await fetch(`${API_BASE_URL}/api/register-push-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionToken}`, // JWT token from login
      },
      body: JSON.stringify({
        token: fcmToken.substring(0, 500), // FCM token, limit to 500 chars
        platform: Capacitor.getPlatform() || 'web',
      }),
    });

    const data = await response.json();

    if (response.ok && data.success) {
      console.log('✅ Push token registered successfully');
      return true;
    }

    console.warn('⚠️ Failed to register push token:', data.message);
    return false;
  } catch (error) {
    console.error('❌ Error registering push token:', error);
    return false;
  }
};

/**
 * Initialize push notifications and get FCM token.
 * The initialization is guarded so auth restoration/login and the mobile shell
 * cannot register duplicate listeners for the same native push session.
 */
export const initializePushNotifications = async (sessionToken) => {
  if (!Capacitor.isNativePlatform()) {
    console.log('ℹ️ Push notifications only supported on native platforms');
    return false;
  }

  if (pushInitPromise) {
    return pushInitPromise;
  }

  if (getPushGuardState() === 'ready' && lastSessionToken === sessionToken) {
    return true;
  }

  pushInitPromise = (async () => {
    try {
      let permission = await PushNotifications.checkPermissions();

      if (permission.receive === 'prompt') {
        permission = await PushNotifications.requestPermissions();
      }

      if (permission.receive === 'denied') {
        console.warn('⚠️ Push notification permission denied by user');
        setPushGuardState(null);
        return false;
      }

      await setupPushNotificationListeners();

      await new Promise((resolve) => {
        registrationListenerHandle = PushNotifications.addListener('registration', async (token) => {
          const fcmToken = token.value;
          if (fcmToken && sessionToken) {
            console.log('🔑 FCM Token obtained:', fcmToken.substring(0, 50) + '...');
            await registerPushToken(fcmToken, sessionToken);
          }
          resolve();
        });

        registrationErrorListenerHandle = PushNotifications.addListener('registrationError', (err) => {
          console.error('❌ Push registration error:', err.error);
          resolve();
        });

        PushNotifications.register().then(() => {
          console.log('✅ Push notifications registered with OS');
        }).catch((err) => {
          console.error('❌ PushNotifications.register() failed:', err);
          resolve();
        });
      });

      lastSessionToken = sessionToken;
      setPushGuardState('ready');
      return true;
    } catch (error) {
      console.error('❌ Error initializing push notifications:', error);
      setPushGuardState(null);
      return false;
    }
  })();

  try {
    return await pushInitPromise;
  } finally {
    pushInitPromise = null;
  }
};

/**
 * Set up listeners for incoming push notifications.
 */
const setupPushNotificationListeners = async () => {
  if (listenersConfigured) {
    return;
  }

  notificationReceivedListenerHandle = await PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log('📬 Push notification received:', notification);
    notifyNotificationHandlers(notification, 'received');
  });

  notificationActionListenerHandle = await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
    console.log('👆 Push notification tapped:', notification);
    notifyNotificationHandlers(notification, 'action');
    handleNotificationTap(notification.notification);
  });

  listenersConfigured = true;
};

/**
 * Handle notification tap action
 */
const handleNotificationTap = (notification) => {
  const data = notification.data || {};
  const notificationType = data.notificationType || 'general';

  console.log('Handling notification type:', notificationType);

  switch (notificationType) {
    case 'gst_verified':
      console.log('🔗 Navigating to partners page');
      if (window.location) {
        window.location.hash = '#/partners';
      }
      break;

    case 'deal_proposed':
      console.log('💼 Navigating to deals page');
      if (window.location) {
        window.location.hash = '#/deals';
      }
      break;

    case 'message':
      console.log('💬 Navigating to messages');
      if (window.location) {
        window.location.hash = '#/messages';
      }
      break;

    default:
      console.log('📌 Generic notification');
  }
};

/**
 * Manually register a new FCM token (for token refresh)
 * Call this if you detect the FCM token has changed
 */
export const refreshPushToken = async (sessionToken) => {
  try {
    if (!Capacitor.isNativePlatform()) {
      console.log('ℹ️ Token refresh only on native platforms');
      return;
    }

    await new Promise((resolve) => {
      PushNotifications.addListener('registration', async (token) => {
        const fcmToken = token.value;
        if (fcmToken && sessionToken) {
          await registerPushToken(fcmToken, sessionToken);
          console.log('🔄 Push token refreshed');
        }
        resolve();
      });

      PushNotifications.addListener('registrationError', (err) => {
        console.error('❌ Push token refresh registration error:', err.error);
        resolve();
      });

      PushNotifications.register().catch((err) => {
        console.error('❌ Push token refresh register() failed:', err);
        resolve();
      });
    });
  } catch (error) {
    console.error('❌ Error refreshing push token:', error);
  }
};

/**
 * Unregister push notifications (call on logout)
 */
export const unregisterPushNotifications = async () => {
  try {
    notificationReceivedListenerHandle?.remove?.();
    notificationActionListenerHandle?.remove?.();
    registrationListenerHandle?.remove?.();
    registrationErrorListenerHandle?.remove?.();

    notificationReceivedListenerHandle = null;
    notificationActionListenerHandle = null;
    registrationListenerHandle = null;
    registrationErrorListenerHandle = null;
    listenersConfigured = false;
    lastSessionToken = null;
    setPushGuardState(null);

    await PushNotifications.unregister();
    console.log('✅ Push notifications unregistered');
  } catch (error) {
    console.error('⚠️ Error unregistering push notifications:', error);
  }
};
